import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Tenant } from "@ares/tokens";

interface TenantContextValue {
  tenant: Tenant;
  setTenant: (tenant: Tenant) => void;
}

export const TenantContext = createContext<TenantContextValue | null>(null);

const STORAGE_KEY = "ares.tenant";
const VALID_TENANTS: Tenant[] = ["ares", "salutem"];

/**
 * Tenant detection cascade (ADR-0006):
 *   1. URL query-param ?tenant=... (highest priority — testing)
 *   2. URL subdomain (klinik.salutem.bio-os.io → salutem)
 *   3. localStorage.ares.tenant (user-pref)
 *   4. fallback: 'ares'
 */
function detectTenant(): Tenant {
  if (typeof window === "undefined") return "ares";

  // 1. Query param
  const params = new URLSearchParams(window.location.search);
  const queryTenant = params.get("tenant");
  if (queryTenant && VALID_TENANTS.includes(queryTenant as Tenant)) {
    return queryTenant as Tenant;
  }

  // 2. Subdomain
  const host = window.location.hostname;
  if (host.includes("salutem")) return "salutem";

  // 3. localStorage
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && VALID_TENANTS.includes(stored as Tenant)) return stored as Tenant;

  // 4. Fallback
  return "ares";
}

interface TenantProviderProps {
  children: ReactNode;
  initialTenant?: Tenant;
}

export function TenantProvider({ children, initialTenant }: TenantProviderProps) {
  const [tenant, setTenantState] = useState<Tenant>(() => initialTenant ?? detectTenant());

  // Mirror to <html data-tenant> for CSS selector hooks.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.dataset.tenant = String(tenant);
    }
  }, [tenant]);

  const setTenant = useCallback((next: Tenant) => {
    setTenantState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    }
  }, []);

  const value = useMemo<TenantContextValue>(() => ({ tenant, setTenant }), [tenant, setTenant]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
